import { Component, HostBinding, OnInit, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { SeoMetadataService } from '@shared/services/seo-metadata.service';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { AuthFirebaseService } from '@shared/services/auth-firebase.service';
import { Store } from '@ngrx/store';
import { AppState } from '@shared/redux/config-store-state.model';
import { emitAlert } from '@shared/redux/actions/actions-store-state.model';
import { JWT_TOKEN } from '@shared/data-structure/jwt-token';

@Component({
  selector: 'astrea-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {

  @HostBinding('class') public hostClass: string = 'h-100';

  // Login
  public formLogin = this.formBuilder.group({
    user: ['', [Validators.required, Validators.email]],
    pass: ['', Validators.required],
  });
  public loginError = false;
  public hide = true;

  // Forgot Password
  public formForgotPass = this.formBuilder.group({
    email: ['', [Validators.required, Validators.email]],
  });
  public forgotPassword = false;

  public loading = false;
  public redirect = '/home';

  constructor(
    private store: Store<AppState>,
    private formBuilder: UntypedFormBuilder,
    private authFirebaseService: AuthFirebaseService,
    private router: Router,
    private route: ActivatedRoute,
    private seo: SeoMetadataService,
    private _t: TranslateService,
    @Inject(PLATFORM_ID) private platformId: string,
  ) { }

  ngOnInit(): void {
    this.setMetadata();
    this.route.queryParams
      .subscribe({
        next: (params: Params) => {
          this.redirect = params.redirect ? params.redirect : '/home';
        }
      });
  }

  setMetadata(): void {
    this.seo.setMetadata({
      title: 'Iniciar sesión | Editorial Astrea',
      description: 'Iniciar sesión con tu cuenta de Astrea.'
    });
  }


  /* ********** COMMON USER ********** */

  async loginUser() {

    if (this.formLogin.valid) {
      this.loading = true;

      const email = this.formLogin.get('user')!.value;
      const pass = this.formLogin.get('pass')!.value;

      this.authFirebaseService.login(email, pass)
        .then(async (data: any) => {

          const user = await this.authFirebaseService.getDataSessionUserPromise();

          if (user && user.token) {
            if (this.isBrowser)
              localStorage.setItem(JWT_TOKEN, user.token);
            setTimeout(() => {
              this.loading = false;
              this.dispatchAlertWelcome();
              this.router.navigate([this.redirect]);
            }, 0);
          } else {
            this.loading = false;
            this.loginError = true;
            this.formLogin.patchValue({ pass: '' });
          }

        })
        .catch(err => {
          this.loading = false;
          this.loginError = true;
          this.formLogin.patchValue({ pass: '' });
        });

    }
  }

  public handleForgotPass() {
    if (this.formForgotPass.valid) {
      this.loading = true;
      this.authFirebaseService.sendEmailChangePass(this.formForgotPass.get('email')!.value)
        .then(() => {
          this.loading = false;
          this.recoverPassOK();
          this.forgotPasswordChange();
        })
        .catch(err => {
          this.loading = false;
          this.recoverPassErr();
        });
    }
  }

  changeTypePass(): void {
    this.hide = !this.hide;
  }

  forgotPasswordChange(): void {
    this.forgotPassword = !this.forgotPassword;
  }

  get isBrowser() {
    return isPlatformBrowser(this.platformId);
  }


  /* ********** ALERTS ********** */

  dispatchAlertWelcome(): void {
    this.store.dispatch(emitAlert({
      message: this._t.instant('alert.welcome'),
      alertType: 'message'
    }));
  }

  recoverPassOK(): void {
    this.store.dispatch(emitAlert({
      message: this._t.instant('alert.forgot_password.ok'),
      alertType: 'success'
    }));
  }

  recoverPassErr(): void {
    this.store.dispatch(emitAlert({
      message: this._t.instant('alert.forgot_password.err'),
      alertType: 'danger'
    }));
  }

}
